import {Injectable, Injector} from "@angular/core";
import {ReportError} from "app/app.service";

import {BaseListService} from "app/services/domain/base.service";
import * as Models from "app/services/proxy/model/models";

import {Lookup} from "framework/services/utils.service";
import {KeyValueChip} from "framework/ui/chips/key-value-chip-list.component";

@Injectable()
export class AppOnboardingPortfolioService extends BaseListService<Models.AppSecopsPortfolioListRequest, Models.BatchRequest, Models.AppSecopsPortfolio>
{
    private readonly m_portfoliosByTenant: Lookup<Models.AppSecopsPortfolio[]>         = {};
    private readonly m_fetchingByTenant: Lookup<Promise<Models.AppSecopsPortfolio[]>>  = {};
    private m_portfolioHierarchy: Lookup<Models.AppSecopsPortfolioHierarchyResponse[]> = {};

    constructor(inj: Injector)
    {
        super(inj, Models.AppSecopsPortfolio);
    }

    @ReportError
    public list(listReq: Models.AppSecopsPortfolioListRequest): Promise<Models.ListResponse>
    {
        return this.domain.apis.appSecOps.listPortfolio(listReq);
    }

    protected async getBatchResponseImpl(req: Models.BatchRequest): Promise<Models.AppSecopsPortfolioBatchResponse>
    {
        const recordIdBatchRequest = Models.BatchRequest.newInstance(req);

        return this.domain.apis.appSecOps.batchPortfolio(recordIdBatchRequest);
    }

    @ReportError
    public async portfolioHierarchy(tenantIds: string[])
    {
        return this.domain.apis.appSecOps.portfolioHierarchy(Models.AppSecopsPortfolioHierarchyRequest.newInstance({tenant_ids: tenantIds}));
    }

    public async getMappedApplications(portfolioIds: string[],
                                       tenantIds: string[]): Promise<Lookup<Models.AppSecopsDashboardApplicationDetails[]>>
    {
        let portfolioApplicationLookup: Lookup<Models.AppSecopsDashboardApplicationDetails[]> = {};
        const portfolioHierarchy                                                              = await this.getPortfolioHierarchy(tenantIds[0]);
        if (portfolioHierarchy?.length)
        {
            portfolioIds.forEach(portfolio =>
                                 {
                                     const mappedPortfolio                 = portfolioHierarchy.find(mappedPortfolio => mappedPortfolio.portfolio_id === portfolio);
                                     portfolioApplicationLookup[portfolio] = mappedPortfolio.applications;
                                 });
        }
        return portfolioApplicationLookup;
    }

    protected createBatchRequest(listReq: Models.AppSecopsPortfolioListRequest,
                                 ids: string[]): Models.BatchRequest
    {
        return Models.BatchRequest.newInstance({ids: ids});
    }

    public portfolioById(portfolioHierarchy: Models.AppSecopsPortfolioHierarchyResponse[],
                         portfolioId: string): Models.AppSecopsPortfolioHierarchyResponse
    {
        return portfolioHierarchy.find(portfolio => portfolio.portfolio_id === portfolioId);
    }

    public async getAllForTenant(tenantId: string): Promise<Models.AppSecopsPortfolio[]>
    {
        if (this.m_portfoliosByTenant[tenantId])
        {
            return this.m_portfoliosByTenant[tenantId];
        }

        if (!this.m_fetchingByTenant[tenantId])
        {
            this.m_fetchingByTenant[tenantId] = (async () =>
            {
                try
                {
                    const ids = await this.listAll(Models.AppSecopsPortfolioListRequest.newInstance({
                                                                                                        filters: Models.AppSecopsPortfolioFilter.newInstance({
                                                                                                                                                                 tenant_id: tenantId
                                                                                                                                                             })
                                                                                                    }));
                    if (ids.length)
                    {
                        this.m_portfoliosByTenant[tenantId] = await this.batchAll(ids);
                    }
                }
                finally
                {
                    delete this.m_fetchingByTenant[tenantId];
                }

                return this.m_portfoliosByTenant[tenantId];
            })();
        }

        return this.m_fetchingByTenant[tenantId];
    }

    public resetTenantCache(tenantId: string)
    {
        this.m_portfoliosByTenant[tenantId] = null;
    }

    public async getAllPortfolioNamesForTenant(tenantId: string): Promise<string[]>
    {
        const allPortfolios = await this.getAllForTenant(tenantId);
        return allPortfolios?.map(portfolio => portfolio.name);
    }

    @ReportError
    public async getPortfolioHierarchy(tenant: string): Promise<Models.AppSecopsPortfolioHierarchyResponse[]>
    {
        if (!this.m_portfolioHierarchy)
        {
            this.m_portfolioHierarchy = {};
        }

        if (this.m_portfolioHierarchy[tenant])
        {
            return Promise.resolve(this.m_portfolioHierarchy[tenant]);
        }

        return this.app.domain.apis.appSecOps.portfolioHierarchy(Models.AppSecopsPortfolioHierarchyRequest.newInstance({tenant_ids: [tenant]}))
                   .then((response: Models.AppSecopsPortfolioHierarchyResponse[]) =>
                         {
                             this.m_portfolioHierarchy[tenant] = response;
                             return response;
                         });
    }

    public resetPortfolioHierarchy(tenantId: string): void
    {
        if (this.m_portfolioHierarchy && this.m_portfolioHierarchy[tenantId])
        {
            delete this.m_portfolioHierarchy[tenantId];
        }
    }
}

export class AppOnboardingBaseFlat<T extends Models.AppSecopsPortfolio | Models.AppSecopsApplication | Models.AppSecopsProject>
{
    constructor(public readonly model: T)
    {}

    get description(): string
    {
        return this.model.description || "";
    }

    get tags(): KeyValueChip[]
    {
        if (this.model.tags?.length)
        {
            return this.model.tags.map(tag => new KeyValueChip(tag.key, tag.value, [], ":"));
        }
        return [];
    }

    get tagsDetailed(): string
    {
        if (this.model.tags?.length)
        {
            return this.model.tags.map(tag => `${tag.key}: ${tag.value}`)
                       .join(",");
        }
        return "";
    }
}
